/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Rendering Monsieur Croissant's replies.
 *
 * One component per block type, and an `UnknownBlock` that renders nothing.
 * That last one matters: the server will grow block types faster than a phone
 * gets updated, and a client that throws on an unrecognised block loses the
 * whole message rather than one card of it.
 *
 * ── ON THE MARKDOWN ─────────────────────────────────────────────────────────
 * There is a small renderer at the bottom rather than a library. It handles
 * bold, italic, inline code, links and lists, and it builds React elements —
 * it never touches dangerouslySetInnerHTML. The model's output is not hostile,
 * but it is text that passed through a model from a web page, and a renderer
 * that cannot emit HTML cannot emit a script tag.
 */

import { Fragment, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import {
  chefApi, type ChefBlock, type ChefNutrition, type ChefRecipeBlock,
} from "../../api/chef.ts";
import { ApiError } from "../../api/client.ts";
import { Icon } from "../../components/Icon.tsx";
import { Spinner } from "../../components/ui.tsx";

interface BlockProps {
  block: ChefBlock;
  /** Post a follow-up as the user. */
  onSend: (text: string) => void;
  /** Something changed on the server; the app's caches are stale. */
  onMutated: () => void;
}

export function ChefBlockView({ block, onSend, onMutated }: BlockProps) {
  switch (block.type) {
    case "text": return <Markdown text={block.markdown} />;
    case "ideas": return <IdeasBlock block={block} onSend={onSend} />;
    case "recipe": return <RecipeBlock block={block} onMutated={onMutated} onSend={onSend} />;
    case "recipeRefs": return <RecipeRefsBlock block={block} />;
    case "plan": return <PlanBlock block={block} />;
    case "shopping": return <ShoppingBlock block={block} />;
    case "substitutions": return <SubstitutionsBlock block={block} />;
    case "table": return <TableBlock block={block} />;
    case "sources": return <SourcesBlock block={block} />;
    case "medicalFlags": return <MedicalFlagsBlock block={block} />;
    case "notice": return <NoticeBlock block={block} />;
    case "proposal": return <ProposalBlock block={block} onMutated={onMutated} />;
    case "chips": return <ChipsBlock block={block} onSend={onSend} />;
    default: return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Ideas
// ─────────────────────────────────────────────────────────────────────────────

function IdeasBlock({ block, onSend }: {
  block: Extract<ChefBlock, { type: "ideas" }>;
  onSend: (t: string) => void;
}) {
  return (
    <div className="space-y-2">
      {block.items.map((idea, i) => (
        <div key={i} className="rounded-lg border border-rule-soft bg-paper p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium text-ink">{idea.title}</p>
              {/* The reason is the point of a suggestion. Without it this is a
                  list, and a list is what a search box gives you. */}
              <p className="mt-0.5 text-xs text-ink-3">{idea.why}</p>
            </div>
            {idea.minutes ? (
              <span className="shrink-0 whitespace-nowrap text-xs text-ink-3">
                {idea.minutes} min
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {idea.recipeId ? (
              <Link
                to={`/recipes/${idea.recipeId}`}
                className="btn-ghost px-2 py-1 text-xs"
              >
                Open recipe
              </Link>
            ) : null}
            <button
              className="btn-ghost px-2 py-1 text-xs"
              onClick={() => onSend(`Add "${idea.title}" to tonight's dinner.`)}
            >
              Add to tonight
            </button>
            <button
              className="btn-ghost px-2 py-1 text-xs"
              onClick={() => onSend(`Tell me more about "${idea.title}".`)}
            >
              Tell me more
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Recipe
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The allergen line.
 *
 * Three states and no fourth, matching AllergenBadge in components/ui.tsx.
 * There is deliberately no green "safe" — the strongest claim this app makes
 * anywhere is "no known conflicts", and a chat surface is exactly where that
 * discipline would erode first if the component allowed it.
 */
function AllergenLine({ block }: { block: ChefRecipeBlock }) {
  if (block.allergenStatus === "conflict") {
    return (
      <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-2.5 py-2 text-xs text-danger">
        <Icon name="alert" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{block.allergenDetail ?? "Conflicts with a household allergy."}</span>
      </div>
    );
  }
  if (block.allergenStatus === "unverified") {
    return (
      <div className="flex items-start gap-2 rounded-md border border-warn/40 bg-warn/10 px-2.5 py-2 text-xs text-warn">
        <Icon name="alert" className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{block.allergenDetail ?? "Some ingredients couldn't be identified."}</span>
      </div>
    );
  }
  return (
    <p className="text-xs text-ink-3">
      No known conflicts with your diet profiles.
    </p>
  );
}

function RecipeBlock({ block, onMutated, onSend }: {
  block: ChefRecipeBlock;
  onMutated: () => void;
  onSend: (t: string) => void;
}) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<"save" | "pdf" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedId, setSavedId] = useState<string | null>(block.recipeId ?? null);

  const meta = [
    `Serves ${block.servings}`,
    block.prepMinutes ? `${block.prepMinutes} min prep` : null,
    block.cookMinutes ? `${block.cookMinutes} min cook` : null,
    block.cuisine, block.difficulty,
  ].filter(Boolean).join(" · ");

  const save = async () => {
    if (!block.draftId) return;
    setBusy("save"); setError(null);
    try {
      const r = await chefApi.saveDraft(block.draftId);
      setSavedId(r.recipeId);
      onMutated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't save that.");
    } finally { setBusy(null); }
  };

  const pdf = async () => {
    setBusy("pdf"); setError(null);
    try {
      if (savedId) await chefApi.downloadRecipePdf(savedId, block.title);
      else if (block.draftId) await chefApi.downloadDraftPdf(block.draftId, block.title);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't make that PDF.");
    } finally { setBusy(null); }
  };

  return (
    <div className="overflow-hidden rounded-xl border border-rule-soft bg-paper">
      <div className="border-b border-rule-soft bg-paper-2 px-3.5 py-3">
        <h3 className="font-serif text-base font-semibold leading-snug text-ink">
          {block.title}
        </h3>
        <p className="mt-0.5 text-xs text-ink-3">{meta}</p>
        {block.dietTags.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {block.dietTags.slice(0, 6).map((t) => (
              <span key={t} className="chip text-[11px]">{t}</span>
            ))}
          </div>
        )}
        <div className="mt-2"><AllergenLine block={block} /></div>
      </div>

      {block.description && (
        <p className="border-b border-rule-soft px-3.5 py-2.5 text-sm text-ink-2">
          {block.description}
        </p>
      )}

      {open && (
        <div className="space-y-3 px-3.5 py-3">
          <section>
            <h4 className="label-mono text-[11px] uppercase tracking-wider text-ink-3">
              Ingredients
            </h4>
            <ul className="mt-1.5 space-y-1">
              {block.ingredients.map((ing, i) => (
                <Fragment key={i}>
                  {ing.groupLabel && ing.groupLabel !== block.ingredients[i - 1]?.groupLabel && (
                    <li className="pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-3">
                      {ing.groupLabel}
                    </li>
                  )}
                  <li className="text-sm text-ink">
                    <span className="tabular-nums text-ink-2">
                      {formatQuantity(ing.quantity, ing.quantityMax, ing.unit)}
                    </span>{" "}
                    {ing.displayName}
                    {ing.prepNote && <span className="text-ink-3">, {ing.prepNote}</span>}
                    {ing.isOptional && <span className="text-ink-3"> (optional)</span>}
                  </li>
                </Fragment>
              ))}
            </ul>
          </section>

          <section>
            <h4 className="label-mono text-[11px] uppercase tracking-wider text-ink-3">
              Method
            </h4>
            <ol className="mt-1.5 space-y-2">
              {block.steps.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-ink">
                  <span className="mt-0.5 shrink-0 font-medium text-accent tabular-nums">
                    {i + 1}
                  </span>
                  <span>
                    {s.text}
                    {s.durationMinutes ? (
                      <span className="ml-1 text-xs text-ink-3">({s.durationMinutes} min)</span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ol>
          </section>

          {/* Inside the expanded body, under the method — the same place it
              sits on the recipe screen. A macro row across the top of a
              collapsed card would make the first thing you see about a
              suggested dinner its calorie count. */}
          {block.nutrition && <NutritionStrip nutrition={block.nutrition} />}

          {block.notes && (
            <p className="border-t border-rule-soft pt-2 text-xs text-ink-2">{block.notes}</p>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5 border-t border-rule-soft bg-paper-2 px-3.5 py-2">
        <button className="btn-ghost px-2 py-1 text-xs" onClick={() => setOpen(!open)}>
          {open ? "Collapse" : "Show recipe"}
        </button>

        {savedId ? (
          <Link to={`/recipes/${savedId}`} className="btn-ghost px-2 py-1 text-xs">
            In your library
          </Link>
        ) : block.draftId ? (
          <button className="btn-ghost px-2 py-1 text-xs" onClick={save} disabled={busy !== null}>
            {busy === "save" ? <Spinner className="h-3 w-3" /> : "Save to library"}
          </button>
        ) : null}

        <button
          className="btn-ghost px-2 py-1 text-xs"
          onClick={() => onSend(`Add "${block.title}" to the plan.`)}
        >
          Add to plan
        </button>

        {block.canExportPdf && (
          <button className="btn-ghost px-2 py-1 text-xs" onClick={pdf} disabled={busy !== null}>
            {busy === "pdf"
              ? <Spinner className="h-3 w-3" />
              : <><Icon name="download" className="mr-1 inline h-3 w-3" />PDF</>}
          </button>
        )}

        {block.model && (
          <span className="ml-auto text-[10px] text-ink-3">{block.model}</span>
        )}
      </div>

      {error && (
        <p className="border-t border-danger/30 bg-danger/5 px-3.5 py-2 text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Per-serving nutrition on a chat recipe card.
 *
 * Renders `value` verbatim. The server formatted these — units, rounding and
 * all — precisely so that this card and the iOS one cannot end up disagreeing
 * about the same recipe, and doing any arithmetic here would give that away.
 *
 * The block simply has no `nutrition` key when there is no estimate, and also
 * when the household has asked not to see figures. Those two cases must stay
 * indistinguishable from here: a "nutrition unavailable" placeholder is still
 * a sentence about calories on a screen that should not have one.
 */
function NutritionStrip({ nutrition }: { nutrition: ChefNutrition }) {
  return (
    <section className="border-t border-rule-soft pt-2">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <h4 className="text-xs font-medium text-ink-2">Per serving</h4>
        <span className="text-[10px] text-ink-3">
          {nutrition.provenance === "published" ? "from the source" : "estimated"}
        </span>
      </div>
      <dl className="flex flex-wrap gap-x-4 gap-y-1">
        {nutrition.items.map((i) => (
          <div key={i.key} className="flex items-baseline gap-1">
            <dt className="text-[11px] text-ink-3">{i.label}</dt>
            <dd className="text-xs font-medium tabular-nums">{i.value}</dd>
          </div>
        ))}
      </dl>
      {/* Usually the line about how much salt was assumed, which is more
          actionable than the sodium figure it qualifies. */}
      {nutrition.note && (
        <p className="mt-1 text-[11px] text-ink-3">{nutrition.note}</p>
      )}
    </section>
  );
}

function formatQuantity(
  q: number | null | undefined, max: number | null | undefined, unit: string | null | undefined,
): string {
  if (q === null || q === undefined) return "";
  const VULGAR: Array<[number, string]> = [
    [0.125, "⅛"], [0.25, "¼"], [1 / 3, "⅓"], [0.375, "⅜"], [0.5, "½"],
    [0.625, "⅝"], [2 / 3, "⅔"], [0.75, "¾"], [0.875, "⅞"],
  ];
  const pretty = (n: number) => {
    const whole = Math.floor(n);
    const frac = n - whole;
    for (const [v, g] of VULGAR) {
      if (Math.abs(frac - v) < 0.02) return whole > 0 ? `${whole}${g}` : g;
    }
    return String(Math.round(n * 100) / 100);
  };
  let out = pretty(q);
  if (max !== null && max !== undefined && max > q) out += `–${pretty(max)}`;
  // "ct" is the internal key for a bare count; showing it puts "3 ct onions"
  // on screen.
  if (unit && unit !== "ct") out += ` ${unit}`;
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// The rest
// ─────────────────────────────────────────────────────────────────────────────

function RecipeRefsBlock({ block }: {
  block: Extract<ChefBlock, { type: "recipeRefs" }>;
}) {
  return (
    <div className="rounded-lg border border-rule-soft bg-paper">
      {block.caption && (
        <p className="border-b border-rule-soft px-3 py-1.5 text-[11px] uppercase tracking-wider text-ink-3">
          {block.caption}
        </p>
      )}
      <ul className="divide-y divide-rule-soft">
        {block.items.map((r) => (
          <li key={r.recipeId}>
            <Link
              to={`/recipes/${r.recipeId}`}
              className="flex items-center justify-between gap-3 px-3 py-2 transition hover:bg-paper-3"
            >
              <span className="min-w-0">
                <span className="block truncate text-sm text-ink">{r.title}</span>
                {r.lastCooked && (
                  <span className="block text-[11px] text-ink-3">{r.lastCooked}</span>
                )}
              </span>
              <span className="shrink-0 text-xs text-ink-3">
                {r.minutes ? `${r.minutes} min` : ""}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PlanBlock({ block }: { block: Extract<ChefBlock, { type: "plan" }> }) {
  return (
    <div className="overflow-hidden rounded-lg border border-rule-soft bg-paper">
      <ul className="divide-y divide-rule-soft">
        {block.days.map((d) => (
          <li key={d.date} className="flex gap-3 px-3 py-2">
            <span className="w-16 shrink-0 text-xs text-ink-3">
              <span className="block font-medium text-ink-2">{d.weekday}</span>
              {d.date.slice(5)}
            </span>
            <span className="min-w-0 flex-1 text-sm">
              {d.entries.length === 0 ? (
                <span className="text-ink-3">Nothing planned</span>
              ) : (
                d.entries.map((e, i) => (
                  <span key={i} className="block text-ink">
                    <span className="text-ink-3">{e.slot}</span> {e.title}
                  </span>
                ))
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ShoppingBlock({ block }: { block: Extract<ChefBlock, { type: "shopping" }> }) {
  return (
    <div className="overflow-hidden rounded-lg border border-rule-soft bg-paper">
      <div className="flex items-center justify-between border-b border-rule-soft px-3 py-1.5">
        <span className="text-xs font-medium text-ink-2">{block.title}</span>
        {block.listId && (
          <Link to={`/shopping/${block.listId}`} className="text-xs text-accent">Open</Link>
        )}
      </div>
      <ul className="max-h-64 divide-y divide-rule-soft overflow-y-auto">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-center gap-2 px-3 py-1.5 text-sm">
            <Icon
              name={it.checked ? "check" : "circle"}
              className={`h-3.5 w-3.5 shrink-0 ${it.checked ? "text-ok" : "text-ink-3"}`}
            />
            <span className={it.checked ? "text-ink-3 line-through" : "text-ink"}>
              {it.quantity !== null && it.quantity !== undefined
                ? `${it.quantity}${it.unit && it.unit !== "ct" ? ` ${it.unit}` : ""} ` : ""}
              {it.name}
            </span>
            {it.aisle && <span className="ml-auto text-[11px] text-ink-3">{it.aisle}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SubstitutionsBlock({ block }: {
  block: Extract<ChefBlock, { type: "substitutions" }>;
}) {
  return (
    <div className="rounded-lg border border-rule-soft bg-paper p-3">
      <p className="text-xs uppercase tracking-wider text-ink-3">
        Instead of {block.forName}
      </p>
      <ul className="mt-1.5 space-y-1.5">
        {block.options.map((o, i) => (
          <li key={i} className="text-sm">
            <span className="font-medium text-ink">{o.toName}</span>
            <span className="text-ink-2"> — {o.ratioNote}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TableBlock({ block }: { block: Extract<ChefBlock, { type: "table" }> }) {
  return (
    <div className="overflow-hidden rounded-lg border border-rule-soft bg-paper">
      {block.caption && (
        <p className="border-b border-rule-soft px-3 py-1.5 text-[11px] uppercase tracking-wider text-ink-3">
          {block.caption}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-rule-soft">
              {block.columns.map((c) => (
                <th key={c} className="px-3 py-1.5 text-left text-[11px] font-medium uppercase tracking-wide text-ink-3">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-rule-soft">
            {block.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-1.5 text-ink">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Where a web answer came from.
 *
 * Rendered quietly but never hidden behind a disclosure. The assistant is
 * allowed to tell you there's a shortage of eggs; it is not allowed to be the
 * reason you believe it. The date matters as much as the links — a price from
 * three weeks ago is a different claim from a price from this morning.
 */
function SourcesBlock({ block }: { block: Extract<ChefBlock, { type: "sources" }> }) {
  return (
    <div className="rounded-lg border border-rule-soft bg-paper-2 px-3 py-2">
      <p className="text-[11px] uppercase tracking-wider text-ink-3">
        Searched the web · {block.searchedOn}
      </p>
      <ul className="mt-1.5 space-y-1">
        {block.items.map((src, i) => (
          <li key={i} className="flex items-start gap-1.5 text-xs">
            <Icon name="link" className="mt-0.5 h-3 w-3 shrink-0 text-ink-3" />
            <a
              href={src.url}
              target="_blank"
              // noopener is not optional on a link the model chose: the target
              // page would otherwise get a handle on this window.
              rel="noreferrer noopener"
              className="min-w-0 break-words text-accent underline decoration-accent/30 hover:decoration-accent"
            >
              {src.title}
              <span className="ml-1 text-ink-3">{hostOf(src.url)}</span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "bbc.co.uk" — the bit that tells you whether to trust the link. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/**
 * Medical considerations for a dish.
 *
 * The colour is driven by `kind` as well as `severity`, because a
 * 'consistency' flag is not a bad thing and must not be painted as one.
 * Warfarin and green vegetables is the case that decides this component:
 * rendering it in red would tell someone to stop eating something they should
 * be eating steadily.
 */
function MedicalFlagsBlock({ block }: {
  block: Extract<ChefBlock, { type: "medicalFlags" }>;
}) {
  const tone = (f: (typeof block.items)[number]) =>
    f.kind === "consistency" || f.kind === "timing"
      ? "border-accent/30 bg-accent-soft text-accent"
      : f.severity === "warn"
        ? "border-danger/40 bg-danger/10 text-danger"
        : "border-warn/40 bg-warn/10 text-warn";

  const label = (f: (typeof block.items)[number]) =>
    f.kind === "consistency" ? "Keep it steady"
      : f.kind === "timing" ? "Timing"
      : f.severity === "warn" ? "Worth knowing"
      : "Note";

  return (
    <div className="space-y-1.5">
      {block.items.map((f, i) => (
        <div key={i} className={`rounded-lg border px-3 py-2.5 text-sm ${tone(f)}`}>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">
              {label(f)}
            </span>
            <span className="font-medium">{f.title}</span>
            {f.people.length > 0 && (
              <span className="text-xs opacity-80">· {f.people.join(", ")}</span>
            )}
          </div>
          {f.matched.length > 0 && (
            <p className="mt-0.5 text-xs opacity-80">
              In this dish: {f.matched.join(", ")}
            </p>
          )}
          <p className="mt-1 text-xs opacity-95">{f.guidance}</p>
        </div>
      ))}
      {/* Said every time, not once in Settings. */}
      <p className="px-1 text-[10px] leading-tight text-ink-3">{block.disclaimer}</p>
    </div>
  );
}

function NoticeBlock({ block }: { block: Extract<ChefBlock, { type: "notice" }> }) {
  const cls = block.tone === "danger"
    ? "border-danger/40 bg-danger/10 text-danger"
    : block.tone === "warn"
      ? "border-warn/40 bg-warn/10 text-warn"
      : "border-accent/30 bg-accent-soft text-accent";
  return (
    <div className={`rounded-lg border px-3 py-2.5 text-sm ${cls}`} role="status">
      <p className="font-medium">{block.title}</p>
      <p className="mt-0.5 text-xs opacity-90">{block.body}</p>
    </div>
  );
}

/**
 * The confirm card.
 *
 * This component is where "the model can change your plan" becomes safe. Until
 * the button is pressed nothing has happened, and the button posts to an
 * endpoint that re-validates every id in the payload. The four states are all
 * shown rather than hidden, because a card that vanishes after you tap it
 * leaves you unsure whether it worked.
 */
function ProposalBlock({ block, onMutated }: {
  block: Extract<ChefBlock, { type: "proposal" }>;
  onMutated: () => void;
}) {
  const [status, setStatus] = useState(block.status);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (which: "apply" | "dismiss") => {
    setBusy(true); setError(null);
    try {
      if (which === "apply") {
        await chefApi.applyProposal(block.proposalId);
        setStatus("applied");
        onMutated();
      } else {
        await chefApi.dismissProposal(block.proposalId);
        setStatus("dismissed");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "That didn't work.");
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-lg border px-3 py-2.5 ${
      status === "applied"
        ? "border-ok/40 bg-ok/5"
        : status === "pending"
          ? "border-accent/40 bg-accent-soft"
          : "border-rule-soft bg-paper-2"}`}
    >
      <p className="text-sm font-medium text-ink">{block.summary}</p>
      {block.detail.length > 0 && (
        <ul className="mt-1 space-y-0.5">
          {block.detail.slice(0, 8).map((d, i) => (
            <li key={i} className="text-xs text-ink-2">{d}</li>
          ))}
          {block.detail.length > 8 && (
            <li className="text-xs text-ink-3">+{block.detail.length - 8} more</li>
          )}
        </ul>
      )}

      {status === "pending" ? (
        <div className="mt-2 flex items-center gap-2">
          <button className="btn-primary px-3 py-1 text-xs" onClick={() => act("apply")} disabled={busy}>
            {busy ? <Spinner className="h-3 w-3" /> : block.confirmLabel}
          </button>
          <button className="btn-ghost px-2 py-1 text-xs" onClick={() => act("dismiss")} disabled={busy}>
            No thanks
          </button>
        </div>
      ) : (
        <p className="mt-1.5 flex items-center gap-1 text-xs text-ink-3">
          {status === "applied" && <><Icon name="check" className="h-3 w-3 text-ok" /> Added</>}
          {status === "dismissed" && "Dismissed"}
          {status === "expired" && "This suggestion expired — ask again and I'll redo it."}
        </p>
      )}

      {error && <p className="mt-1.5 text-xs text-danger">{error}</p>}
    </div>
  );
}

function ChipsBlock({ block, onSend }: {
  block: Extract<ChefBlock, { type: "chips" }>;
  onSend: (t: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {block.items.map((c, i) => (
        <button
          key={i}
          className="rounded-full border border-rule-soft bg-paper px-3 py-1.5 text-xs text-ink-2 transition hover:border-accent/40 hover:bg-accent-soft hover:text-accent"
          onClick={() => onSend(c.send)}
        >
          {c.label}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Minimal markdown
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Bold, italic, inline code, links and lists — nothing else, and no HTML.
 *
 * Small enough to read in a minute, which is the argument for it over a
 * library: the entire attack surface of a component that renders model output
 * is visible on one screen, and it is React elements all the way down.
 */
export function Markdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const out: ReactNode[] = [];
  let list: ReactNode[] = [];

  const flush = () => {
    if (!list.length) return;
    out.push(
      <ul key={`l${out.length}`} className="my-1 list-disc space-y-0.5 pl-5">{list}</ul>);
    list = [];
  };

  lines.forEach((line, i) => {
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      list.push(<li key={i} className="text-sm">{inline((bullet ?? numbered)![1]!)}</li>);
      return;
    }
    flush();
    if (line.trim()) {
      out.push(
        <p key={i} className="text-sm leading-relaxed text-ink">{inline(line)}</p>);
    }
  });
  flush();

  return <div className="space-y-1.5">{out}</div>;
}

function inline(text: string): ReactNode[] {
  // One pass, one regex, alternation ordered so ** beats *.
  const RE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\((?:https?:\/\/|\/)[^)\s]+\))/g;
  const out: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;

  while ((m = RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      out.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("`")) {
      out.push(
        <code key={k++} className="rounded bg-paper-3 px-1 py-0.5 text-[0.9em]">
          {tok.slice(1, -1)}
        </code>);
    } else if (tok.startsWith("[")) {
      const cut = tok.indexOf("](");
      const label = tok.slice(1, cut);
      const href = tok.slice(cut + 2, -1);
      // Internal links go through the router; external ones get noopener.
      out.push(href.startsWith("/")
        ? <Link key={k++} to={href} className="text-accent underline">{label}</Link>
        : <a key={k++} href={href} target="_blank" rel="noreferrer noopener"
             className="text-accent underline">{label}</a>);
    } else {
      out.push(<em key={k++}>{tok.slice(1, -1)}</em>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
